import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { X, Settings, Cookie } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { getLocalizedRoute } from '@/lib/routes';

export const CookieConsentBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false
  });
  const { language } = useLanguage();

  const content = {
    en: {
      title: "Cookie Settings",
      description: "We use cookies to enhance your browsing experience and provide personalized content.",
      essential: "Essential Cookies",
      essentialDesc: "Required for website functionality",
      analytics: "Analytics Cookies", 
      analyticsDesc: "Help us understand site usage",
      marketing: "Marketing Cookies",
      marketingDesc: "Used for advertising and personalization",
      acceptAll: "Accept All",
      acceptSelected: "Accept Selected",
      rejectAll: "Reject All",
      customize: "Customize",
      learnMore: "Learn More About Our Cookie Policy"
    },
    es: {
      title: "Configuración de Cookies",
      description: "Utilizamos cookies para mejorar su experiencia de navegación y proporcionar contenido personalizado.",
      essential: "Cookies Esenciales",
      essentialDesc: "Requeridas para la funcionalidad del sitio web",
      analytics: "Cookies Analíticas",
      analyticsDesc: "Nos ayudan a entender el uso del sitio",
      marketing: "Cookies de Marketing", 
      marketingDesc: "Utilizadas para publicidad y personalización",
      acceptAll: "Aceptar Todas",
      acceptSelected: "Aceptar Seleccionadas", 
      rejectAll: "Rechazar Todas",
      customize: "Personalizar",
      learnMore: "Saber Más Sobre Nuestra Política de Cookies"
    }
  };

  const current = content[language];

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      // Show banner after a short delay
      setTimeout(() => setIsVisible(true), 2000);
    }
  }, []);

  const handleAcceptAll = () => {
    const consentData = {
      essential: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie-consent', JSON.stringify(consentData));
    
    // Enable Google Analytics and other tracking
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: 'granted'
      });
    }
    
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const consentData = {
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie-consent', JSON.stringify(consentData));
    
    // Disable non-essential tracking
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied'
      });
    }
    
    setIsVisible(false);
  };

  const handleAcceptSelected = () => {
    const consentData = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie-consent', JSON.stringify(consentData));
    
    // Update consent based on preferences
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        analytics_storage: preferences.analytics ? 'granted' : 'denied',
        ad_storage: preferences.marketing ? 'granted' : 'denied'
      });
    }
    
    setIsVisible(false);
  };

  const togglePreference = (type: 'analytics' | 'marketing') => {
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-background/95 backdrop-blur-sm border-t">
      <Card className="max-w-4xl mx-auto shadow-xl">
        <CardContent className="p-6">
          {!showSettings ? (
            // Simple consent banner
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex items-center gap-3 flex-1">
                <Cookie className="w-6 h-6 text-primary flex-shrink-0" />
                <div>
                  <h3 className="font-semibold mb-1">{current.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    {current.description}{' '}
                    <Link to={getLocalizedRoute('cookiePolicy', language)} className="text-primary underline">
                      {current.learnMore}
                    </Link>
                  </p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSettings(true)}
                >
                  {current.customize}
                </Button>
                <Button
                  variant="outline" 
                  size="sm"
                  onClick={handleRejectAll}
                >
                  {current.rejectAll}
                </Button>
                <Button
                  size="sm"
                  onClick={handleAcceptAll}
                >
                  {current.acceptAll}
                </Button>
              </div>
            </div>
          ) : (
            // Detailed settings panel
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">{current.title}</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSettings(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-4 mb-6">
                {/* Essential Cookies */}
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <h4 className="font-medium">{current.essential}</h4>
                    <p className="text-sm text-muted-foreground">{current.essentialDesc}</p>
                  </div>
                  <div className="text-sm font-medium text-muted-foreground">
                    Always On
                  </div>
                </div>
                
                {/* Analytics Cookies */}
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <h4 className="font-medium">{current.analytics}</h4>
                    <p className="text-sm text-muted-foreground">{current.analyticsDesc}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={() => togglePreference('analytics')}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
                
                {/* Marketing Cookies */}
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <h4 className="font-medium">{current.marketing}</h4>
                    <p className="text-sm text-muted-foreground">{current.marketingDesc}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={() => togglePreference('marketing')}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRejectAll}
                >
                  {current.rejectAll}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAcceptSelected}
                >
                  {current.acceptSelected}
                </Button>
                <Button
                  size="sm"
                  onClick={handleAcceptAll}
                >
                  {current.acceptAll}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};